import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { EMPTY, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { SharedNotifications } from 'src/app/_shared/classes/shared-notifications';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ActivityNotificationsTypes } from 'src/app/_shared/stores/home/home.state';
import { MenuState } from 'src/app/_user/store/menu/menu.state';
import { InformationsActiviteHomeNotification } from 'src/graphql/generated-types';

@Component({
    selector: 'app-activity-notifications-list',
    templateUrl: './activity-notifications-list.component.html',
    styleUrls: ['./activity-notifications-list.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ActivityNotificationsListComponent extends SharedNotifications<ActivityNotificationsTypes> implements OnInit {

    activityNotificationsTypes = ActivityNotificationsTypes;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly layoutService: LayoutService,
                private readonly dialogService: KalDialogService) {
        super(cdr);
    }

    navigate(notificationType: ActivityNotificationsTypes): void {
        this.watch$ = this.processNavigation$(notificationType);
    }

    private getRoutePathFromNotificationType(notificationType: ActivityNotificationsTypes): string[] {
        switch (notificationType) {

            case ActivityNotificationsTypes.DELIVERABLE_CLIENTS_ORDERS:
                return ['activity', 'sales', 'deliveries', 'deliver'];
            case ActivityNotificationsTypes.PENDING_CLIENTS_ORDERS:
            case ActivityNotificationsTypes.INVOICABLE_CLIENTS_ORDERS:
                return ['activity', 'sales', 'orders', 'list'];

            case ActivityNotificationsTypes.PAYABLE_CLIENTS_INVOICES:
                return ['activity', 'sales', 'invoices', 'list'];

            case ActivityNotificationsTypes.SUPPLIER_PART_A_CHARGE_RECORDS_INVOICEABLE:
                return ['activity', 'benefits', 'pac', 'orders', 'suppliers', 'list'];

            case ActivityNotificationsTypes.PENDING_GIFTS_RECORDS:
            case ActivityNotificationsTypes.PENDING_PART_A_CHARGE_RECORDS:
            case ActivityNotificationsTypes.PENDING_REFUND_RECORDS:
            case ActivityNotificationsTypes.GIFTS_DELIVERABLE:
                return ['activity', 'benefits', 'records', 'list'];

            case ActivityNotificationsTypes.PAYABLE_SUPPLIERS_INVOICES:
                return ['activity', 'sales', 'suppliers', 'invoices', 'list'];

            case ActivityNotificationsTypes.RECEIVABLE_SUPPLIERS_ORDERS:
                return ['activity', 'sales', 'suppliers', 'orders', 'list'];

            case ActivityNotificationsTypes.PENDING_SAVINGS:
            case ActivityNotificationsTypes.PROCESSABLE_SAVINGS:
            case ActivityNotificationsTypes.PAYABLE_SAVINGS_ORDERS:
                return ['activity', 'savings', 'record', 'list']

            case ActivityNotificationsTypes.MEDIA_OVERDUE:
            case ActivityNotificationsTypes.MEDIA_DELIVRABLE:
                return ['activity', 'media-libraries', 'bookings', 'list'];

            case ActivityNotificationsTypes.RESOURCE_OVERDUE:
            case ActivityNotificationsTypes.RESOURCE_DELIVRABLE:
                return ['activity', 'shared-resources', 'bookings', 'list'];
        }
    }

    private processNavigation$(notificationType: ActivityNotificationsTypes): Observable<number> {
        return this.getMenuId(notificationType)
            .pipe(
                switchMap(menuId =>
                    this.setNotificationType(notificationType).pipe(
                        map(() => menuId)
                    )
                ),
                tap(menuId => {
                    this.layoutService.navigate({
                        content: this.getRoutePathFromNotificationType(notificationType),
                        focus: 'empty'
                    }, {
                        primaryRoute: 'content',
                        queryParams: {idMenu: menuId}
                    });
                })
            );
    }

    private getMenuId(notificationType: ActivityNotificationsTypes): Observable<number> {
        let menuCode = null;

        switch (notificationType) {
            case ActivityNotificationsTypes.DELIVERABLE_CLIENTS_ORDERS:
                menuCode = 'SalesDeliver';
                break;
            case ActivityNotificationsTypes.PENDING_CLIENTS_ORDERS:
            case ActivityNotificationsTypes.INVOICABLE_CLIENTS_ORDERS:
                menuCode = 'SalesList';
                break;
            case ActivityNotificationsTypes.PAYABLE_CLIENTS_INVOICES:
                menuCode = 'SalesInvoicesList';
                break;
            case ActivityNotificationsTypes.SUPPLIER_PART_A_CHARGE_RECORDS_INVOICEABLE:
                menuCode = 'BenefitPacSuppliersSalesList';
                break;
            case ActivityNotificationsTypes.PENDING_GIFTS_RECORDS:
            case ActivityNotificationsTypes.PENDING_PART_A_CHARGE_RECORDS:
            case ActivityNotificationsTypes.PENDING_REFUND_RECORDS:
            case ActivityNotificationsTypes.GIFTS_DELIVERABLE:
                menuCode = 'BenefitRecordManagment';
                break;
            case ActivityNotificationsTypes.PAYABLE_SUPPLIERS_INVOICES:
                menuCode = 'SalesSupplierInvoicesList';
                break;
            case ActivityNotificationsTypes.RECEIVABLE_SUPPLIERS_ORDERS:
                menuCode = 'SalesSupplierOrdersList';
                break;

            case ActivityNotificationsTypes.PENDING_SAVINGS:
            case ActivityNotificationsTypes.PROCESSABLE_SAVINGS:
            case ActivityNotificationsTypes.PAYABLE_SAVINGS_ORDERS:
                menuCode = 'SavingsRecordsList';
                break;

            case ActivityNotificationsTypes.MEDIA_OVERDUE:
            case ActivityNotificationsTypes.MEDIA_DELIVRABLE:
                menuCode = 'MediaLibraryReservationsList';
                break;

            case ActivityNotificationsTypes.RESOURCE_OVERDUE:
            case ActivityNotificationsTypes.RESOURCE_DELIVRABLE:
                menuCode = 'SharedRessourcesReservationsList';
                break;
        }

        const activityId = +(this.selectedNotification.Informations as InformationsActiviteHomeNotification).IdActivite;
        const menuId = this.layoutService.getMenuFromCode(menuCode, activityId);

        if (!menuId) {
            const dialogConfig = new KalDialogConfig<ConfirmDialogData>({
                title: 'Impossible d\'accéder à la page',
                hasBackdrop: true,
                backdropClass: 'cdk-overlay-dark-backdrop',
                data: {
                    body: 'Votre profil ne vous permet pas d\'accéder à cette page.',
                    submitLabel: 'Fermer',
                }
            });

            return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed
                .pipe(
                    switchMap(() => EMPTY)
                );
        }

        return of(menuId);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

}
